import type { FeatureFlagKey } from '@sb/feathers-types/featureFlags';
import type { CartesianPose } from '@sb/geometry';
import { distanceBetweenPoses, quaternionAngle } from '@sb/geometry';
import { makeNamespacedLog } from '@sb/log';
import type { ArmTarget } from '@sb/motion-planning';
import type { RoutineContext } from '@sb/routine-runner/RoutineContext';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import type { StatefulEventEmitter } from '@sb/utilities';

const log = makeNamespacedLog('WaypointReachedTracker');

// Distance threshold to determine if a waypoint has been reached
const RELAXED_THRESHOLD_DIST = 0.1; // 10cm

export const STRICT_THRESHOLD_DIST = 0.002; // 2mm

// Orientation thresholds (radians) to determine if a waypoint has been reached
const RELAXED_THRESHOLD_ANGLE_RAD = (5 * Math.PI) / 180; // ~5°
const STRICT_THRESHOLD_ANGLE_RAD = (1 * Math.PI) / 180; // ~1°

// Interval to check if a waypoint has been reached
export const WAYPOINT_REACHED_CHECK_INTERVAL_MS = 25;

// 1mm minimum increase to consider "moving away"
const MIN_DISTANCE_INCREASE = 0.001;

/**
 * This class is a helper class for RoutineContext to track if a waypoint has
 * been reached based on the current pose and the target list.  This is to solve
 * the issue when we pause and resume during a motion with multiple waypoints.
 * This class is separated to keep the logic separate from the RoutineContext class.
 *
 * This class works by checking the current pose against first target in the list.
 * Once the distance is below a threshold, the target is considered visited, and
 * the next target is checked.  This continues until all targets are visited.
 *
 * The tracker supports both wrist frame and tooltip frame targets based on the
 * toolFrame parameter. It compares poses in the same frame (wrist-to-wrist or
 * tooltip-to-tooltip).
 */
export class WaypointReachedTracker {
  private targets: ArmTarget[];

  private events: StatefulEventEmitter<any>;

  private routineContext: RoutineContext;

  private toolFrame: string | undefined;

  private nextTargetIndex: number = 0;

  private nextTargetPose: CartesianPose | null = null;

  private nextTargetPoseDistThreshold: number = RELAXED_THRESHOLD_DIST;

  private cancelPauseListener: () => void;

  private cancelBeginMotionListener: () => void;

  private cancelResumeListener: () => void;

  private checkWaypointReachedInterval: ReturnType<typeof setInterval> | null =
    null;

  // use index as key to avoid recomputing the pose for the same target
  private targetPoseCache: Map<number, CartesianPose> = new Map();

  private previousDist: number = Infinity;

  private checkWaypointImmediately: boolean = true;

  private isDeviceActuationPending: () => boolean = () => false;

  // Private constructor, use WaypointReachedTracker.create instead
  private constructor({
    targets,
    events,
    routineContext,
    toolFrame,
    checkWaypointImmediately = true,
    isDeviceActuationPending = () => false,
  }: {
    targets: ArmTarget[];
    events: StatefulEventEmitter<any>;
    routineContext: RoutineContext;
    toolFrame?: string;
    checkWaypointImmediately?: boolean;
    isDeviceActuationPending?: () => boolean;
  }) {
    this.targets = targets;
    this.events = events;
    this.routineContext = routineContext;
    this.toolFrame = toolFrame;
    this.checkWaypointImmediately = checkWaypointImmediately;
    this.isDeviceActuationPending = isDeviceActuationPending;

    // Store cancel functions for each listener so we can cancel them later
    this.cancelPauseListener = this.events.on('pause', () => {
      this.clearCheckWaypointReachedInterval();
    });

    this.cancelBeginMotionListener = this.events.on('beginMotion', () => {
      this.setCheckWaypointReachedInterval();
    });

    // If motion is resumed after a pause, restart waypoint checking
    this.cancelResumeListener = this.events.on('resume', () => {
      // Check if device actuation is in progress - if so, don't start waypoint checking yet
      const state = this.routineContext.getRoutineRunnerState();

      const isDeviceActuationInProgress =
        state.kind === 'RoutineRunning' &&
        state.deviceActuationState?.inProgress;

      if (isDeviceActuationInProgress) {
        return;
      }

      this.setCheckWaypointReachedInterval();
    });

    // Allow external recalibration trigger after device movements
    this.events.on('recalibrateAfterDeviceMovement', async () => {
      await this.recalibrateAfterDeviceMovement();
    });
  }

  // Async static factory to ensure tracker is initialized before returning
  public static async create(params: {
    targets: ArmTarget[];
    events: StatefulEventEmitter<any>;
    routineContext: RoutineContext;
    toolFrame?: string;
    checkWaypointImmediately?: boolean;
    /**
     * While true, no waypoint is reported: a lift move fired at the previous
     * waypoint is still travelling, so the next one has not been reached yet.
     */
    isDeviceActuationPending?: () => boolean;
  }): Promise<WaypointReachedTracker> {
    const tracker = new WaypointReachedTracker(params);
    await tracker.init();

    return tracker;
  }

  private async init() {
    try {
      await this.updateNextTargetPose();
    } catch (error) {
      log.error(
        'init.failed',
        'Failed to initialize WaypointReachedTracker:',
        error,
      );

      throw error;
    }
  }

  public destroy() {
    this.clearCheckWaypointReachedInterval();
    this.cancelPauseListener();
    this.cancelBeginMotionListener();
    this.cancelResumeListener();
  }

  public async compensateNewTargets(newTargets: ArmTarget[]): Promise<void> {
    this.targets = newTargets;
    this.targetPoseCache.clear();
    this.previousDist = Infinity;
    this.nextTargetIndex = 0;
    this.nextTargetPose = null;
    this.nextTargetPoseDistThreshold = RELAXED_THRESHOLD_DIST;
    await this.updateNextTargetPose();
  }

  /**
   * Sets interval to call the checkWaypointReached function
   */
  public setCheckWaypointReachedInterval() {
    if (this.nextTargetIndex >= this.targets.length) {
      log.info(
        'setCheckWaypointReachedInterval',
        'All targets reached, not setting check waypoint reached interval',
      );

      return;
    }

    log.info(
      'setCheckWaypointReachedInterval',
      `Setting check waypoint reached for ${this.targets.length} points with interval ${WAYPOINT_REACHED_CHECK_INTERVAL_MS}ms`,
    );

    this.clearCheckWaypointReachedInterval();

    // if we are using waypoints, run the check to see if we have reached a waypoint
    this.checkWaypointReachedInterval = setInterval(async () => {
      await this.checkWaypointReached();
    }, WAYPOINT_REACHED_CHECK_INTERVAL_MS);
  }

  /**
   * Clears the checkWaypointReached interval
   */
  public clearCheckWaypointReachedInterval() {
    if (!this.checkWaypointReachedInterval) {
      return;
    }

    log.info(
      'clearCheckWaypointReachedInterval',
      'Clearing check waypoint reached interval',
    );

    clearInterval(this.checkWaypointReachedInterval);
    this.checkWaypointReachedInterval = null;
  }

  public isChecking(): boolean {
    return this.checkWaypointReachedInterval !== null;
  }

  /**
   * Return the list of targets that have not been reached yet.  We do simplier
   * approach of just return the targets from our target list, instead
   * of actually comparing the passed target list to the our own target list.
   * So this could possibly break if getUpdatedTargets is using a different list
   */
  public getUpdatedTargets(_: ArmTarget[]): ArmTarget[] {
    // This has been changes to allow for empty list returns for updated targets.
    // This allows full traversal of a single guided mode motion.
    return this.targets.slice(this.nextTargetIndex);
  }

  public async incrementNextTargetIndex() {
    await this.onWaypointReached();

    return this.nextTargetIndex;
  }

  /**
   * Index into the original target list of the next unreached target — the
   * offset `getUpdatedTargets` slices from, used to map indexes in a replan's
   * target list back to the original list.
   */
  public get reachedTargetCount(): number {
    return this.nextTargetIndex;
  }

  /** Index of the next waypoint not yet reported as reached. */
  public get nextIndex(): number {
    return this.nextTargetIndex;
  }

  /**
   * Report the next unreported waypoint as reached. For the runner to finish a
   * plan whose final goal has succeeded, one lift move at a time.
   */
  public async reportNextWaypointReached(): Promise<void> {
    if (this.nextTargetIndex >= this.targets.length) {
      return;
    }

    await this.onWaypointReached();
  }

  /**
   * Check if all waypoints have been completed.
   */
  public get allWaypointsCompleted(): boolean {
    return this.nextTargetIndex >= this.targets.length;
  }

  /**
   * Re-calibrate target poses after device movement
   * This should be called after the lift has moved to ensure spatial accuracy
   */
  public async recalibrateAfterDeviceMovement(): Promise<void> {
    log.info(
      'recalibrateAfterDeviceMovement',
      'Re-calibrating target poses after device movement',
    );

    // Clear the pose cache since positions have changed
    this.targetPoseCache.clear();

    // Reset the previous distance to force fresh distance calculations
    this.previousDist = Infinity;

    // Re-calculate the current target pose with the new robot position
    if (this.nextTargetIndex < this.targets.length) {
      await this.updateNextTargetPose();
    }

    log.info('recalibrateAfterDeviceMovement', 'Re-calibration completed');
  }

  /**
   * Get current pose in the appropriate frame (wrist or tooltip) based on toolFrame.
   * If toolFrame is 'tool0' or undefined, returns wrist pose.
   * Otherwise, returns tooltip pose.
   */
  private getCurrentPose(): CartesianPose {
    const state = this.routineContext.getRoutineRunnerState().kinematicState;
    const useWristFrame = !this.toolFrame || this.toolFrame === 'tool0';

    return useWristFrame ? state.wristPose : state.tooltipPoint;
  }

  /**
   * Get target pose in the appropriate frame.
   * Targets are expected to already be in the correct frame (wrist or tooltip).
   * For pose targets, directly returns the pose.
   * For joint targets, uses FK and returns the pose in the appropriate frame.
   */
  private async getTargetPose(index: number): Promise<CartesianPose> {
    // Cache the target poses for this tracker instance
    if (!this.targetPoseCache.has(index)) {
      const target = this.targets[index];
      let pose: CartesianPose;

      // Check if it's a joint target - need FK
      if ('jointAngles' in target) {
        const fkResult =
          await this.routineContext.hybridMotionPlanner.forwardKinematics({
            jointAngles: target.jointAngles,
            gripperOpenness: 1,
            checkValidity: false,
          });

        const useWristFrame = !this.toolFrame || this.toolFrame === 'tool0';

        // FK returns tooltip pose, apply equipment offset to get wrist if needed
        const poseWithOffset = this.routineContext.addEquipmentOffset(
          fkResult.pose,
        );

        pose = useWristFrame
          ? poseWithOffset.wristPose
          : poseWithOffset.tcpPose;
      } else if ('pose' in target) {
        // For pose targets, assume they're already in the correct frame
        pose = target.pose;
      } else {
        throw new Error('Unknown target type in WaypointReachedTracker');
      }

      this.targetPoseCache.set(index, pose);
    }

    return this.targetPoseCache.get(index)!;
  }

  /**
   * Check to see if we have reached the next waypoint.
   */
  private async checkWaypointReached() {
    // Ignore checks while paused or during device actuations
    const state = this.routineContext.getRoutineRunnerState();

    if (
      state.kind === 'RoutineRunning' &&
      (state.isPaused || state.deviceActuationState?.inProgress)
    ) {
      return;
    }

    if (this.isDeviceActuationPending()) {
      return;
    }

    if (this.nextTargetIndex >= this.targets.length) {
      this.clearCheckWaypointReachedInterval();

      return;
    }

    if (this.nextTargetPose == null) {
      // All waypoints have been completed, stop checking
      this.clearCheckWaypointReachedInterval();

      return;
    }

    // Compare poses in the same frame (wrist-to-wrist or tooltip-to-tooltip)
    // based on the toolFrame parameter
    const currentPose = this.getCurrentPose();

    const dist = distanceBetweenPoses(currentPose, this.nextTargetPose!);

    // Orientation difference (radians) between current and target
    const angle = quaternionAngle(currentPose, this.nextTargetPose!);

    const oldPreviousDist = this.previousDist;
    this.previousDist = dist;

    // we consider the waypoint reached if:
    // 1. distance is less than strict threshold, OR
    // 2. distance is <= relaxed threshold AND robot is moving away
    const ORIENTATION_FLAG = 'waypointOrientationGate' as FeatureFlagKey;

    const orientationGateEnabled = getFeatureFlagSync(ORIENTATION_FLAG, false);

    const reachedByDistanceOnly =
      (dist <= this.nextTargetPoseDistThreshold &&
        dist - oldPreviousDist >= MIN_DISTANCE_INCREASE) ||
      dist < STRICT_THRESHOLD_DIST;

    // Orientation-gated conditions
    let reachedWithOrientation = false;

    if (orientationGateEnabled) {
      const angleOkStrict = angle < STRICT_THRESHOLD_ANGLE_RAD;
      const angleOkRelaxed = angle < RELAXED_THRESHOLD_ANGLE_RAD;

      reachedWithOrientation =
        ((dist <= this.nextTargetPoseDistThreshold &&
          dist - oldPreviousDist >= MIN_DISTANCE_INCREASE &&
          angleOkRelaxed) ||
          (dist < STRICT_THRESHOLD_DIST && angleOkStrict)) === true;
    }

    const reached = orientationGateEnabled
      ? reachedWithOrientation
      : reachedByDistanceOnly;

    if (reached) {
      log.info('checkWaypointReached', 'waypoint reached', {
        dist,
        angle,
        nextTargetPoseDistThreshold: this.nextTargetPoseDistThreshold,
        previousDist: oldPreviousDist,
      });

      await this.onWaypointReached();
    }
  }

  private async onWaypointReached() {
    this.events.emit('waypointReached', this.nextTargetIndex);

    this.nextTargetIndex += 1;

    await this.updateNextTargetPose();
  }

  private async getClampedBlendRadius(
    index: number,
    pose: CartesianPose,
  ): Promise<number> {
    // This function clamps the blend radius to be the min of distAB / 2 and distBC / 2
    // where A, B, C are the targets at index - 1, index, index + 1. This matches the logic
    // in r2cb.
    // standard_bots_cartesian_planning/include/standard_bots_cartesian_planning/segments/hybrid_blending.hpp#L706
    const targetB = this.targets[index];
    const poseB = pose; // pose for the this.targets[index]; this avoids recomputing the pose
    const blendRadius = targetB.blendRadius ?? 0;

    // For single targets just return the blend radius
    if (index === 0 && this.targets.length === 1) {
      return blendRadius;
    }

    // don't need to check dist AB for first target
    if (index === 0) {
      const poseC = await this.getTargetPose(index + 1);
      const distBC = distanceBetweenPoses(poseB, poseC);

      return Math.min(distBC / 2, blendRadius);
    }

    // don't need to check dist BC for last target
    if (index === this.targets.length - 1) {
      const poseA = await this.getTargetPose(index - 1);

      const distAB = distanceBetweenPoses(poseA, poseB);

      return Math.min(distAB / 2, blendRadius);
    }

    // for non-first or non-last targets, we need to compute poses for A, B, C to calculate
    // the min of distAB / 2 and distBC / 2
    const [poseA, poseC] = await Promise.all([
      this.getTargetPose(index - 1),
      this.getTargetPose(index + 1),
    ]);

    const distAB = distanceBetweenPoses(poseA, poseB);
    const distBC = distanceBetweenPoses(poseB, poseC);
    const maxRadius = Math.min(distAB, distBC) / 2;

    return Math.min(maxRadius, blendRadius);
  }

  private async updateNextTargetPose() {
    if (this.nextTargetIndex < this.targets.length) {
      try {
        this.nextTargetPose = await this.getTargetPose(this.nextTargetIndex);

        const blendRadius = await this.getClampedBlendRadius(
          this.nextTargetIndex,
          this.nextTargetPose,
        );

        this.nextTargetPoseDistThreshold = Math.max(
          blendRadius,
          RELAXED_THRESHOLD_DIST,
        );

        this.previousDist = Infinity;

        log.info(
          'updateNextTargetPose',
          `Updated next waypoint index to ${this.nextTargetIndex} of ${this.targets.length - 1} with dist threshold ${this.nextTargetPoseDistThreshold.toFixed(4)}m`,
          {
            clampedBlendRadius: blendRadius,
          },
        );

        // Only perform immediate check if interval is currently active
        if (this.checkWaypointImmediately && this.isChecking()) {
          await this.checkWaypointReached();
        }
      } catch (error) {
        log.error(
          'updateNextTargetPose.failed',
          'Failed to update next target pose',
          error,
        );
      }
    } else {
      // All waypoints have been completed
      this.nextTargetPose = null;
      this.nextTargetPoseDistThreshold = Infinity;
    }
  }
}
